import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type {
  AuthContextType,
  AuthCredentials,
  AuthLoginResponse,
  AuthUser,
} from '@/types/auth.types.ts'
import { loginUser as loginRequest } from '@/services/api/authClient.ts'
import {
  clearAuthSession,
  getAuthSession,
  setAuthSession,
} from '@/utils/apiAuthUtils.ts'

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  roles: [],
  login: () => Promise.reject(new Error('AuthProvider not initialized')),
  logout: () => {},
})

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AuthLoginResponse | null>(() =>
    getAuthSession(),
  )

  useEffect(() => {
    const synchronizeAuthSession = () => {
      setSession(getAuthSession())
    }

    window.addEventListener('storage', synchronizeAuthSession)

    return () => {
      window.removeEventListener('storage', synchronizeAuthSession)
    }
  }, [])

  const login = useCallback(async (credentials: AuthCredentials) => {
    const authResponse = await loginRequest(credentials)
    setAuthSession(authResponse)
    setSession(authResponse)
    return authResponse
  }, [])

  const logout = useCallback(() => {
    clearAuthSession()
    setSession(null)
  }, [])

  const user: AuthUser | null = useMemo(
    () =>
      session
        ? {
            userId: session.user_id,
            email: session.email,
            firstName: session.first_name,
            lastName: session.last_name,
          }
        : null,
    [session],
  )

  const value: AuthContextType = useMemo(
    () => ({
      isAuthenticated: Boolean(session),
      user,
      roles: session?.roles ?? [],
      login,
      logout,
    }),
    [login, logout, session, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export { AuthContext }
